/**
 * client/src/lib/payment-rail/officer-key-derivation.ts
 *
 * L0-CORE pure crypto primitive: deterministically derive an officer's hybrid
 * recipient key pair (ML-KEM-768 + ECDH-P256) from a WebAuthn PRF output, for
 * use as a `RecipientPubkeys` target in shared/crypto/envelope.ts (#955).
 *
 * The PRF output is the SAME hardware-bound IKM used by derivePrfVaultKek, so
 * the officer never stores a long-lived private key — the recipient secret keys
 * are re-derived on demand inside the WebAuthn ceremony and wiped after use.
 *
 * Security invariants:
 *   - L0-9: prfOutput + every transient (mlkemSeed, ecRaw) are atomicWipe()'d
 *     in a finally — secrets are zeroed even on error.
 *   - L0-6 / Rule 11: HKDF-SHA256 from the PRF output is the ONLY derivation
 *     path; no password, no PBKDF2.
 *   - AC-OKD-10: secret bytes (mlkemSk / ecSk) leave the module ONLY via the
 *     returned typed buffers — never JSON.stringify'd or base64-encoded here.
 *   - AC-OKD-05: HKDF info labels are a FROZEN wire contract; changing them
 *     changes every officer's derived identity.
 *
 * Pure / isomorphic — uses @noble primitives + WebCrypto only. No Node/DOM
 * specifics, no I/O, no fetch.
 *
 * (NIST SP 800-108r1 HKDF / RFC 9380 §5 hash-to-field / FIPS-203 ML-KEM)
 */

import { hkdf }   from "@noble/hashes/hkdf";
import { sha256 } from "@noble/hashes/sha256";
import { ml_kem768 } from "@noble/post-quantum/ml-kem";
import { p256 } from "@noble/curves/p256";
import { bytesToNumberBE, numberToBytesBE } from "@noble/curves/abstract/utils";

// Reuse the single canonical wipe impl (Centralized Modules rule). Re-exported
// AND invoked through this module's own namespace (`self.atomicWipe`) so the
// L0-9 wipe seam can be observed via vi.spyOn without any production-only hook.
import { atomicWipe, hexToBytes } from "../prf-key-derivation";
import { bytesToBase64 } from "../pqc";
import * as self from "./officer-key-derivation";
export { atomicWipe };

/** Single reused TextEncoder for the two FROZEN HKDF info labels. */
const TEXT_ENCODER = new TextEncoder();

// ---------------------------------------------------------------------------
// Public contract — pin EXACTLY (consumed by #955)
// ---------------------------------------------------------------------------

export type OfficerKeyPair = {
  /** ML-KEM-768 public key, base64. */
  mlkemPubkey: string;
  /** P-256 ECDH public key, base64, uncompressed 0x04 point. */
  ecdhPubkey: string;
  /**
   * ML-KEM-768 secret key. The CALLER is responsible for wiping this with
   * atomicWipe() after use (same contract as derivePrfVaultKek's return).
   */
  mlkemSk: Uint8Array;
  /**
   * P-256 secret scalar, 32-byte big-endian. The CALLER wipes this with
   * atomicWipe() after use.
   */
  ecSk: Uint8Array;
};

/** FROZEN HKDF context label for the ML-KEM half — must match on all clients. */
export const OFFICER_MLKEM_INFO = "strem/officer/mlkem/v1";
/** FROZEN HKDF context label for the P-256 half — must match on all clients. */
export const OFFICER_EC_INFO = "strem/officer/ec/v1";

/** ml_kem768.keygen requires a 64-byte deterministic seed. */
const MLKEM_SEED_BYTES = 64;

/**
 * RFC 9380 §5: to sample a uniform scalar mod n with negligible bias, draw
 * L = ceil((ceil(log2(p)) + k) / 8) bytes, where k = 128-bit security margin.
 * For P-256 (256-bit n): L = ceil((256 + 128) / 8) = 48. Using a raw 32-byte
 * HKDF output as the scalar is an out-of-range / modulo-biased bug — a security
 * persona blocks on it — so we oversize-then-reduce.
 */
const EC_SCALAR_OVERSIZE_BYTES = 48;

// ---------------------------------------------------------------------------
// reduceToP256Scalar — oversize-then-reduce into [1, n-1] (AC-OKD-03)
// ---------------------------------------------------------------------------

/**
 * Reduce a 48-byte big-endian HKDF output into a valid P-256 private scalar in
 * the range [1, n-1].
 *
 * Map: scalar = (rawBE mod (n-1)) + 1.  This maps the full 48-byte space onto
 * exactly the (n-1) valid private keys with negligible bias (RFC 9380 §5), and
 * guarantees the result is never 0 and never >= n. Exported so AC-OKD-03 can
 * drive the >= n and = 0 reduce paths directly without mocking p256.
 */
export function reduceToP256Scalar(ecRaw: Uint8Array): Uint8Array {
  const n = p256.CURVE.n;
  const reduced = (bytesToNumberBE(ecRaw) % (n - 1n)) + 1n; // → [1, n-1]
  return numberToBytesBE(reduced, 32);
}

// ---------------------------------------------------------------------------
// deriveOfficerKeyPair — the derivation primitive
// ---------------------------------------------------------------------------

/**
 * Deterministically derive an officer's hybrid recipient key pair from a
 * WebAuthn PRF output.
 *
 * @param prfOutput  Raw 32-byte PRF output from the WebAuthn assertion.
 *                   THIS BUFFER IS WIPED AFTER THE CALL (L0-9). Hold no other
 *                   reference to it.
 * @param prfSalt    The hex PRF eval input (from the server profile); decoded
 *                   to bytes and used as the HKDF salt to domain-separate the
 *                   officer keys from the vault KEK.
 *
 * @returns OfficerKeyPair — the CALLER wipes the returned mlkemSk / ecSk.
 */
export function deriveOfficerKeyPair(prfOutput: Uint8Array, prfSalt: string): OfficerKeyPair {
  const saltBytes = hexToBytes(prfSalt);

  let mlkemSeed: Uint8Array | undefined;
  let ecRaw: Uint8Array | undefined;
  try {
    // --- ML-KEM-768 half ---
    mlkemSeed = hkdf(
      sha256,
      prfOutput,
      saltBytes,
      TEXT_ENCODER.encode(OFFICER_MLKEM_INFO),
      MLKEM_SEED_BYTES,
    );
    const kem = ml_kem768.keygen(mlkemSeed);

    // --- P-256 half (oversize-then-reduce, RFC 9380 §5) ---
    ecRaw = hkdf(
      sha256,
      prfOutput,
      saltBytes,
      TEXT_ENCODER.encode(OFFICER_EC_INFO),
      EC_SCALAR_OVERSIZE_BYTES,
    );
    const ecSk = reduceToP256Scalar(ecRaw);
    const ecdhPub = p256.getPublicKey(ecSk, false); // uncompressed 0x04 point

    return {
      mlkemPubkey: bytesToBase64(kem.publicKey),
      ecdhPubkey: bytesToBase64(ecdhPub),
      mlkemSk: kem.secretKey,
      ecSk,
    };
  } finally {
    // L0-9: zero the PRF output and every transient secret, even on error.
    // The returned mlkemSk / ecSk are NOT wiped here — that is the caller's job.
    // Dispatched via `self.atomicWipe` so the wipe is observable (vi.spyOn) —
    // do not inline to a bare atomicWipe().
    self.atomicWipe(prfOutput);
    if (mlkemSeed) self.atomicWipe(mlkemSeed);
    if (ecRaw) self.atomicWipe(ecRaw);
  }
}
